/**
 * @module SAH
 */
import Animations from './utils/animations.js';
import './modules/datepicker.js';
import './modules/grid-helper.js';

((Drupal, once) => {
  // Smooth scroll behavior
  Drupal.behaviors.ScrollDownSmooth = {
    attach: (context) => {
      const scrollToHash = (hash, delay = 0) => {
        const target = document.querySelector(hash);
        if (target) {
          setTimeout(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, delay);
        }
      };

      const anchors = context.querySelectorAll('a[href^="#"], a[href^="/#"]');
      for (const anchor of anchors) {
        anchor.addEventListener('click', (e) => {
          const hash = anchor.hash;
          if (hash) {
            const target = document.querySelector(hash);
            if (target && !target.closest('.modal')) {
              e.preventDefault();
              // Hide menu on mobile
              document.body.classList.remove('show-burger-menu');
              scrollToHash(hash);
            }
          }
        });
      }

      // If page loads with a hash, scroll after short delay
      if (location.hash && document.querySelector(location.hash)) {
        const hash = location.hash;
        history.replaceState(null, '', ' ');
        scrollToHash(hash, 200);
      }
    },
  };

  // Messages close button
  Drupal.behaviors.MessagesCloseBtn = {
    attach: (context) => {
      const messages = once(
        'statusMsgX',
        '#status-messages > div[role="contentinfo"]',
        context,
      );
      for (const msg of messages) {
        const closeBtn = document.createElement('span');
        closeBtn.className = 'close-btn';
        closeBtn.addEventListener('click', () => msg.remove());
        msg.appendChild(closeBtn);
      }
    },
  };

  // Burger menu toggle
  Drupal.behaviors.ShowBurgerNavi = {
    attach: () => {
      const buttons = once('burger-menu', '.header__burger');
      for (const button of buttons) {
        button.addEventListener('click', () => {
          const isOpen = document.body.classList.toggle('show-burger-menu');
          button.setAttribute('aria-expanded', String(isOpen));
        });
      }
    },
  };

  // Local tasks menu toggle
  Drupal.behaviors.LocalTasksToggle = {
    attach: (context) => {
      const toggleButtons = once(
        'local-tasks-toggle',
        '.local-tasks__toggle',
        context,
      );
      for (const button of toggleButtons) {
        const wrapper = button.closest('.local-tasks__wrapper');
        const links = wrapper.querySelectorAll('.local-tasks__navigation a');

        button.addEventListener('click', () => {
          const isExpanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', !isExpanded);
          wrapper.setAttribute('data-expanded', !isExpanded);
        });

        // Close menu when clicking on a link
        for (const link of links) {
          link.addEventListener('click', () => {
            button.setAttribute('aria-expanded', 'false');
            wrapper.setAttribute('data-expanded', 'false');
          });
        }

        // Close menu when clicking outside
        document.addEventListener('click', (e) => {
          if (!wrapper.contains(e.target)) {
            button.setAttribute('aria-expanded', 'false');
            wrapper.setAttribute('data-expanded', 'false');
          }
        });

        // Close menu on Escape key
        document.addEventListener('keydown', (e) => {
          if (
            e.key === 'Escape' &&
            button.getAttribute('aria-expanded') === 'true'
          ) {
            button.setAttribute('aria-expanded', 'false');
            wrapper.setAttribute('data-expanded', 'false');
            button.focus();
          }
        });
      }
    },
  };

  // FAQ items toggle
  Drupal.behaviors.FAQitems = {
    attach: (context) => {
      const items = once('faqShow', '.paragraph-faq-item', context);
      for (const [i, item] of items.entries()) {
        const text = item.querySelector('.field-text');

        if (i > 0) {
          item.classList.add('paragraph-faq-item--hidden');
          text.style.display = 'none';
        }

        const toggle = () => {
          item.classList.toggle('paragraph-faq-item--hidden');
          // Replace direct style manipulation with slideToggle animation
          Animations.slideToggle(text, 300);
        };

        item.addEventListener('click', toggle);
      }
    },
  };

  // Course restart confirmation dialog
  Drupal.behaviors.CourseRestartDialog = {
    attach: (context) => {
      const finishLinks = once(
        'course-finish-dialog',
        '.course-finish',
        context,
      );
      const dialog = document.getElementById('course-restart-dialog');

      if (!dialog) return;

      let targetUrl = null;

      // Handle clicks on finish links
      for (const link of finishLinks) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          targetUrl = link.href;
          dialog.showModal();
        });
      }

      // Handle form submission
      const form = dialog.querySelector('form[method="dialog"]');
      if (form) {
        form.addEventListener('submit', (e) => {
          if (e.submitter?.value === 'yes' && targetUrl) {
            window.location.href = targetUrl;
          }
          targetUrl = null;
        });
      }

      // Fallback for browser-native dialog close
      dialog.addEventListener('close', () => {
        if (dialog.returnValue === 'yes' && targetUrl) {
          window.location.href = targetUrl;
        }
        targetUrl = null;
      });
    },
  };

  // Video modal close
  Drupal.behaviors.coursePartHideVideoModal = {
    attach: (context) => {
      const buttons = once('video-close', '.course-video__close', context);
      for (const btn of buttons) {
        btn.addEventListener('click', () => {
          document.body.classList.remove('fixed');
          const wrapper = btn.closest('.course-video');
          wrapper.classList.remove('visible', 'ready');
          setTimeout(() => {
            const video = wrapper.querySelector('#course-part-video');
            if (video) video.innerHTML = '';
          }, 300);
        });
      }
    },
  };

  // Modal controls
  const modalHandler = {
    show: (context) => {
      const triggers = once('modal-trigger', '.modal__trigger', context);
      for (const trigger of triggers) {
        trigger.addEventListener('click', () => {
          const modalId = trigger.dataset.target;
          const modal = document.getElementById(modalId);
          if (modal) {
            modal.classList.add('is-open');
            document.body.classList.add('fixed');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      }
    },
    close: (context) => {
      const closeButtons = once('modal-close', '.modal__close', context);
      for (const btn of closeButtons) {
        btn.addEventListener('click', () => {
          const modal = btn.closest('.modal');
          if (modal) {
            modal.classList.remove('is-open');
            const triggers = document.querySelectorAll(
              `[data-target="${modal.id}"]`,
            );
            for (const t of triggers) t.setAttribute('aria-expanded', 'false');

            if (!document.querySelector('.modal.is-open')) {
              document.body.classList.remove('fixed');
            }
          }
        });
      }
    },
    global: (context) => {
      for (const _ of once('modal-global-listeners', 'html', context)) {
        // Close on clicking outside (backdrop)
        document.addEventListener('click', (e) => {
          if (
            e.target.classList.contains('modal') &&
            e.target.classList.contains('is-open')
          ) {
            const modal = e.target;
            modal.classList.remove('is-open');
            const triggers = document.querySelectorAll(
              `[data-target="${modal.id}"]`,
            );
            for (const t of triggers) t.setAttribute('aria-expanded', 'false');

            if (!document.querySelector('.modal.is-open')) {
              document.body.classList.remove('fixed');
            }
          }
        });

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            const openModal = document.querySelector('.modal.is-open');
            if (openModal) {
              openModal.classList.remove('is-open');
              const triggers = document.querySelectorAll(
                `[data-target="${openModal.id}"]`,
              );
              for (const t of triggers)
                t.setAttribute('aria-expanded', 'false');
              document.body.classList.remove('fixed');
            }
          }
        });
      }
    },
  };

  Drupal.behaviors.showModalBlock = { attach: modalHandler.show };
  Drupal.behaviors.closeModalBlock = { attach: modalHandler.close };
  Drupal.behaviors.globalModalBlock = { attach: modalHandler.global };

  // Contact modal — opens form in a modal when #kontakt target is absent
  Drupal.behaviors.ContactModal = {
    attach: (context) => {
      const links = once(
        'contact-modal',
        '.header__nav-actions a[href="#kontakt"]',
        context,
      );

      for (const link of links) {
        link.addEventListener('click', (e) => {
          const kontaktEl = document.getElementById('kontakt');
          if (kontaktEl && !kontaktEl.closest('.modal')) return;
          e.preventDefault();
          e.stopImmediatePropagation();

          const existing = document.getElementById('contact-modal');
          if (existing) {
            existing.classList.add('is-open');
            document.body.classList.add('fixed');
            return;
          }

          link.classList.add('is-loading');

          fetch(Drupal.url('bgm-contact/modal-form'), {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          })
            .then((response) => {
              if (!response.ok) throw new Error(response.statusText);
              return response.json();
            })
            .then((data) => {
              const modal = document.createElement('div');
              modal.className = 'modal modal--contact';
              modal.id = 'contact-modal';
              modal.innerHTML = `
                <div class="modal__content">
                  <button type="button" class="modal__close" aria-label="${Drupal.t('Close')}">&times;</button>
                  <div class="modal__body">${data.html}</div>
                </div>`;

              const main = document.querySelector('main') || document.body;
              main.appendChild(modal);
              Drupal.attachBehaviors(modal);

              modal.classList.add('is-open');
              document.body.classList.add('fixed');
            })
            .catch((error) => {
              console.error('Contact modal:', error);
            })
            .finally(() => {
              link.classList.remove('is-loading');
            });
        });
      }
    },
  };

  // Scroll-based navigation hide
  Drupal.behaviors.HideNavi = {
    attach: () => {
      const html = document.documentElement;

      const checkScroll = () => {
        const st = window.scrollY || document.documentElement.scrollTop;
        html.classList.toggle('hide-navi', st > 50);
      };

      window.addEventListener('scroll', checkScroll, { passive: true });
      checkScroll();
    },
  };

  // Cyberfitness components
  Drupal.behaviors.Cyberfitnessitems = {
    attach: (context) => {
      // Copy to clipboard
      const spans = once(
        'cyberfitness-code',
        '.block-cyberfitness .field-code h1 span',
        context,
      );
      for (const span of spans) {
        span.addEventListener('click', async () => {
          await navigator.clipboard.writeText(span.textContent);
        });
      }

      // Toggle items
      const toggles = once(
        'cyberfitness-toggle',
        '.block-cyberfitness .toggle-items',
        context,
      );
      for (const btn of toggles) {
        btn.addEventListener('click', () => {
          const content = btn.previousElementSibling;
          content.classList.toggle('hide');
          // Replace direct style manipulation with slideToggle animation
          Animations.slideToggle(content, 300);
        });
      }
    },
  };

  // Auto-hide messages
  Drupal.behaviors.HideMessages = {
    attach: (context) => {
      const containers = once('fadeOutStatus', '#status-messages', context);
      for (const container of containers) {
        setTimeout(() => {
          // Replace inline style opacity manipulation with fadeOut animation
          Animations.fadeOut(container, 1000, () => container.remove());
        }, 5000);
      }
    },
  };

  // Contact form height adjustment
  Drupal.behaviors.SetContactFormHeight = {
    attach: (context) => {
      const adjustHeight = () => {
        const blocks = document.querySelectorAll('.block-contact', context);
        for (const block of blocks) {
          const form = block.querySelector('.contact-form');
          if (!form) continue;

          const blockHeight = block.offsetHeight;
          const formHeight = form.offsetHeight;
          const margin = formHeight - blockHeight + 100;

          block.style.marginTop = margin > 0 ? `${margin}px` : '';
          block.style.marginBottom = margin > 0 ? `${margin}px` : '';
        }
      };

      window.addEventListener('resize', adjustHeight, { passive: true });
      adjustHeight();
    },
  };

  // BEF Checkboxes behavior
  Drupal.behaviors.BefCheckboxesBehavior = {
    attach: (context) => {
      const checkboxGroups = once(
        'bef-checkboxes',
        '.form-checkboxes.bef-checkboxes',
        context,
      );

      for (const checkboxes of checkboxGroups) {
        const allButton = document.createElement('div');
        allButton.className = 'form-item js-form-type-checkbox button--get-all';
        allButton.innerHTML = `<label class="option">${Drupal.t('Select all')}</label>`;
        checkboxes.prepend(allButton);

        const getInputs = () => [
          ...checkboxes.querySelectorAll('input[type="checkbox"]'),
        ];
        const isAnyChecked = () => getInputs().some((input) => input.checked);

        const syncButtonState = () => {
          allButton.classList.toggle('checked', !isAnyChecked());
        };

        allButton.addEventListener('click', () => {
          // Already in "all" state — nothing to do, skip the AJAX call
          if (!isAnyChecked()) return;

          for (const input of getInputs()) {
            input.checked = false;
          }
          syncButtonState();
          // Notify Views AJAX. Targeting the last unchecked input avoids any
          // race with BEF listeners that might re-check the first input.
          getInputs()
            .at(-1)
            ?.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Delegated — one listener for the whole group, not one per checkbox.
        // Also catches dynamically injected checkboxes after AJAX re-renders.
        checkboxes.addEventListener('change', (e) => {
          if (e.target.matches('input[type="checkbox"]')) {
            syncButtonState();
          }
        });

        syncButtonState();
      }
    },
  };
})(Drupal, once);

// HMR handling
if (import.meta.hot) {
  import.meta.hot.accept(() => console.log('HMR'));
}
